import React, { useState } from 'react';
import './ContactForm.css';
import Botpoison from '@botpoison/browser';

// Create Botpoison instance with public key
const botpoison = new Botpoison({
  publicKey: 'pk_14465247-0c53-4f05-88a8-52c36f0e3258'
});

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    if (!formData.consent) {
      setError('Please accept the consent agreement to proceed.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      const response = await fetch('https://submit-form.com/luYac6Zvq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          consent: formData.consent,
          form_type: 'Contact Us',
          // Recipient email - configure this endpoint to send to info@sanmateo-orthodontist.com
          _email: {
            to: 'info@sanmateo-orthodontist.com',
            subject: `New Contact Form Submission: ${formData.subject}`
          },
          // Add Botpoison solution
          _botpoison: solution
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          consent: false
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (err) {
      setError('There was an error submitting your message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="contact-container">
        <div className="form-success">
          <div className="success-icon">✓</div>
          <h3>Message Sent Successfully!</h3>
          <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
          <button
            className="cta-button secondary"
            onClick={() => setIsSubmitted(false)}
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-container">
      <div className="contact-layout">
        {/* Contact Form Section - Left */}
        <div className="contact-form-section">
          <div className="form-header">
            <h3>Send Us a Message</h3>
            <p>We'll get back to you within 24 hours</p>
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact-name">Name *</label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-email">Email *</label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact-phone">Phone</label>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-subject">Subject *</label>
                <select
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Appointment Request">Appointment Request</option>
                  <option value="Insurance Questions">Insurance Questions</option>
                  <option value="Treatment Information">Treatment Information</option>
                  <option value="Emergency">Emergency</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="contact-message">Message *</label>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={5}
              />
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleInputChange}
                  required
                />
                <span className="checkmark"></span>
                I accept the above information will be used to contact me.
              </label>
            </div>

            {error && (
              <div className="form-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="cta-button primary form-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
            </button>
          </form>
        </div>

        {/* Right Side - Map and Contact Info */}
        <div className="right-side">
          {/* Map Section - Upper Right */}
          <div className="map-container">
            <div className="map-content">
              <div className="map-pin">📍</div>
              <h4>Conveniently Located Near You</h4>
              <p>256 North San Mateo Drive<br />San Mateo, CA 94401</p>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3162.8234567890123!2d-122.3123456789!3d37.5678901234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808f9e123456789a%3A0x123456789abcdef0!2s256%20N%20San%20Mateo%20Dr%2C%20San%20Mateo%2C%20CA%2094401!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="250"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="San Mateo Orthodontics Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Info Section - Lower Right */}
          <div className="contact-info-card">
            <div className="contact-details">
              <h3>Contact Information</h3>

              <div className="contact-item">
                <div className="contact-icon">👨‍⚕️</div>
                <div>
                  <strong>Dr. Tom Ellerhorst DDS, MSD</strong>
                  <p>Board Certified Orthodontist</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div>
                  <strong>Address</strong>
                  <p>256 North San Mateo Drive, Suite #1<br />San Mateo, CA 94401</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div>
                  <strong>Phone</strong>
                  <p>(650) 343-3603</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">🕒</div>
                <div>
                  <strong>Office Hours</strong>
                  <p>Mon-Thu: 8:00 AM - 5:30 PM<br />Fri: 8:00 AM - 2:30 PM</p>
                </div>
              </div>

              <div className="emergency-note">
                <p><strong>Emergency appointments available</strong><br />
                  Call our office for urgent care needs.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm; 